import type { PortfolioNavLink } from '../ui/PortfolioNav'
import type { ProcessingVariant } from '../ui/ProcessingBadge'
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildHowToSchema,
  buildSoftwareApplicationSchema,
  type FaqItem,
} from '../seo/schema'
import {
  buildCanonical,
  toSeoProps,
  type SeoRouteConfig,
  type UmbrellaSeoRoutes,
} from '../seo/routes'
import type { SeoProps } from '../seo/Seo'

export type UtilityAvailability = 'available' | 'coming-soon' | 'hidden'
export type UtilityProcessingMode = ProcessingVariant

export interface UtilityNavMetadata {
  label: string
  description?: string
  category?: string
  order?: number
  hidden?: boolean
}

export interface UtilityProcessingMetadata {
  mode: UtilityProcessingMode
  label?: string
  title?: string
}

export interface UtilityRelatedTool<RouteKey extends string = string> {
  routeKey: RouteKey
  label: string
  href?: string
  description?: string
}

export interface UtilitySchemaMetadata {
  applicationCategory?: string
  breadcrumbLabel?: string
  softwareName?: string
  softwareDescription?: string
  howTo?: readonly string[]
  faq?: readonly FaqItem[]
}

export interface UtilityRouteDefinition<
  RouteKey extends string = string,
  RelatedRouteKey extends string = RouteKey,
> {
  routeKey: RouteKey
  path: string
  title: string
  description: string
  nav: UtilityNavMetadata
  seo: Omit<SeoRouteConfig, 'path'>
  processing: UtilityProcessingMetadata
  analyticsKey?: string
  availability: UtilityAvailability
  schema?: UtilitySchemaMetadata
  relatedTools?: readonly UtilityRelatedTool<RelatedRouteKey>[]
}

export function defineUtilityRegistry<const Registry extends readonly UtilityRouteDefinition[]>(
  registry: Registry,
): Registry {
  return registry
}

export function buildUtilitySeoRoutes<RouteKey extends string>(
  domain: string,
  registry: readonly UtilityRouteDefinition<RouteKey>[],
): UmbrellaSeoRoutes<RouteKey> {
  return {
    domain,
    routes: Object.fromEntries(
      registry
        .filter((route) => route.availability === 'available')
        .map((route) => [
          route.routeKey,
          {
            path: route.path,
            ...route.seo,
            jsonLd: {
              mode: 'required',
              schemas: buildUtilityToolSchemas({ domain, registry, routeKey: route.routeKey }),
            },
          } satisfies SeoRouteConfig,
        ]),
    ) as unknown as Record<RouteKey, SeoRouteConfig>,
  }
}

export function buildUtilityRoutePathMap<RouteKey extends string>(
  registry: readonly UtilityRouteDefinition<RouteKey>[],
): Record<RouteKey, string> {
  return Object.fromEntries(
    registry.map((route) => [route.routeKey, route.path]),
  ) as Record<RouteKey, string>
}

export function buildUtilityNavItems<RouteKey extends string>(
  registry: readonly UtilityRouteDefinition<RouteKey>[],
  currentRouteKey: RouteKey,
): PortfolioNavLink[] {
  return [...registry]
    .filter((route) => route.availability === 'available' && !route.nav.hidden)
    .sort((a, b) => (a.nav.order ?? 0) - (b.nav.order ?? 0))
    .map((route) => ({
      key: route.routeKey,
      label: route.nav.label,
      href: route.path,
      current: route.routeKey === currentRouteKey,
    }))
}

export interface UtilityAnalyticsAttributionRoute {
  path: string
  analyticsKey: string
}

export function resolveUtilitySourceToolKey(
  registry: readonly UtilityAnalyticsAttributionRoute[],
  pathname: string,
): string | undefined {
  const canonicalPath = canonicalAttributionPath(pathname)
  return registry.find((route) => canonicalAttributionPath(route.path) === canonicalPath)
    ?.analyticsKey
}

function canonicalAttributionPath(value: string): string {
  const pathname = new URL(value, 'https://portfolio.invalid').pathname
  return pathname.replace(/\/+$/, '') || '/'
}

export function utilitySeoFor<RouteKey extends string>(
  domain: string,
  registry: readonly UtilityRouteDefinition<RouteKey>[],
  routeKey: RouteKey,
): SeoProps {
  const route = findUtilityRoute(registry, routeKey)

  return toSeoProps(domain, {
    path: route.path,
    ...route.seo,
  })
}

export interface UtilitySchemaOptions<RouteKey extends string = string> {
  domain: string
  registry: readonly UtilityRouteDefinition<RouteKey>[]
  routeKey: RouteKey
  siteName?: string
}

export function buildUtilityToolSchemas<RouteKey extends string>({
  domain,
  registry,
  routeKey,
  siteName,
}: UtilitySchemaOptions<RouteKey>): object[] {
  const route = findUtilityRoute(registry, routeKey)
  const canonical = buildCanonical(domain, route.path)
  const schema = route.schema
  const schemas: object[] = [
    buildBreadcrumbSchema([
      { name: siteName ?? domain, url: buildCanonical(domain, '/') },
      { name: schema?.breadcrumbLabel ?? route.title, url: canonical },
    ]),
    buildSoftwareApplicationSchema({
      name: schema?.softwareName ?? route.title,
      description: schema?.softwareDescription ?? route.description,
      category: schema?.applicationCategory ?? 'UtilitiesApplication',
      url: canonical,
    }),
  ]

  if (schema?.howTo && schema.howTo.length > 0) {
    schemas.push(buildHowToSchema(route.title, schema.howTo))
  }

  if (schema?.faq && schema.faq.length > 0) {
    schemas.push(buildFaqSchema(schema.faq))
  }

  return schemas
}

export function findUtilityRoute<RouteKey extends string>(
  registry: readonly UtilityRouteDefinition<RouteKey>[],
  routeKey: RouteKey,
): UtilityRouteDefinition<RouteKey> {
  const route = registry.find((item) => item.routeKey === routeKey)

  if (!route) {
    throw new Error(`Unknown utility route: ${routeKey}`)
  }

  return route
}
